import { buildUrlPath } from './resolveUrl';
import { RECONNECT_DELAY_MS } from './reconnect-policy';
import { createPresenterConfigMessage, decodePresentationFrame, encodePresentationFrame, getMessageType, parseLiveInputAudio, PresentationMessageTypes, } from './presentation-protocol';
import { AdminClientMessageTypes, WebSocketSubscribe, WebSocketConnectionQuery, } from 'api/event-state';
import { deriveConnectionHealth, HEALTH_CHECK_INTERVAL_MS, HeartbeatMessageTypes, PING_INTERVAL_MS, shouldForceReconnect, } from './ws-heartbeat';
export class EventWsClient {
    eventId;
    subscribe;
    presenterId;
    module;
    admin;
    preferredAudioSourceDeviceId;
    socket = null;
    intentionalClose = false;
    reconnectTimer = null;
    pingTimer = null;
    healthCheckTimer = null;
    pingSeq = 0;
    lastServerActivityMs = 0;
    firstOpenMs = 0;
    connectionReady = false;
    lastHealth = 'connecting';
    onPresentationMessage = null;
    adminHandlers = {};
    onLiveInputAudio = null;
    onOpen = null;
    onClose = null;
    onHealthChange = null;
    constructor(options) {
        if (typeof options === 'string') {
            this.eventId = options;
            this.subscribe = [];
            this.module = false;
            this.admin = false;
        }
        else {
            this.eventId = options.eventId;
            this.subscribe = options.subscribe ?? [];
            this.presenterId = options.presenterId;
            this.module = options.module ?? false;
            this.admin = options.admin ?? false;
            this.preferredAudioSourceDeviceId = options.preferredAudioSourceDeviceId;
        }
    }
    connect(handlers) {
        this.onPresentationMessage = handlers.onPresentationMessage ?? null;
        this.adminHandlers = handlers.admin ?? {};
        this.onOpen = handlers.onOpen;
        this.onClose = handlers.onClose;
        this.onHealthChange = handlers.onHealthChange ?? null;
        this.intentionalClose = false;
        this.openSocket();
    }
    disconnect() {
        this.intentionalClose = true;
        this.stopHeartbeat();
        if (this.reconnectTimer !== null) {
            window.clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        this.socket?.close();
        this.socket = null;
        this.connectionReady = false;
        this.lastServerActivityMs = 0;
        this.firstOpenMs = 0;
        this.updateHealth('connecting');
    }
    sendPresenterConfig(language, textToSpeech) {
        if (this.socket?.readyState !== WebSocket.OPEN) {
            return;
        }
        this.socket.send(createPresenterConfigMessage(language, textToSpeech));
    }
    sendAudioSourcesSync(audioSources) {
        if (this.socket?.readyState !== WebSocket.OPEN) {
            return;
        }
        this.socket.send(JSON.stringify({
            type: AdminClientMessageTypes.AudioSourcesSync,
            audioSources,
        }));
    }
    sendAudioUpload(pcm) {
        if (this.socket?.readyState !== WebSocket.OPEN) {
            return;
        }
        const metadataJson = JSON.stringify({
            type: AdminClientMessageTypes.AudioUpload,
        });
        this.socket.send(encodePresentationFrame(metadataJson, new Uint8Array(pcm)));
    }
    sendLiveInputAudioMonitor(enabled) {
        if (this.socket?.readyState !== WebSocket.OPEN) {
            return;
        }
        this.socket.send(JSON.stringify({
            type: AdminClientMessageTypes.LiveInputAudioMonitor,
            enabled,
        }));
    }
    setLiveInputAudioHandler(handler) {
        this.onLiveInputAudio = handler;
    }
    openSocket() {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const params = new URLSearchParams({ eventId: this.eventId });
        if (this.subscribe.length > 0) {
            params.set('subscribe', this.subscribe.join(','));
        }
        if (this.presenterId !== undefined) {
            params.set('presenterId', this.presenterId);
        }
        if (this.module) {
            params.set(WebSocketConnectionQuery.Module, '1');
        }
        if (this.admin) {
            params.set(WebSocketConnectionQuery.Admin, '1');
        }
        if (this.preferredAudioSourceDeviceId !== undefined && this.preferredAudioSourceDeviceId !== '') {
            params.set(WebSocketConnectionQuery.PreferredAudioSourceDeviceId, this.preferredAudioSourceDeviceId);
        }
        const wsUrl = `${protocol}//${window.location.host}${buildUrlPath('/ws')}?${params.toString()}`;
        this.socket = new WebSocket(wsUrl);
        this.socket.binaryType = 'arraybuffer';
        this.socket.onopen = () => {
            this.connectionReady = true;
            this.firstOpenMs = Date.now();
            this.lastServerActivityMs = 0;
            this.startHeartbeat();
            this.updateHealth(this.computeHealth());
            this.onOpen?.();
        };
        this.socket.onmessage = (event) => {
            this.recordServerActivity();
            if (typeof event.data === 'string') {
                this.handleTextMessage(event.data);
                return;
            }
            if (!(event.data instanceof ArrayBuffer)) {
                return;
            }
            const decoded = decodePresentationFrame(event.data);
            if (getMessageType(decoded.metadataJson) === PresentationMessageTypes.LiveInputAudio) {
                const bytes = parseLiveInputAudio(decoded.metadataJson, decoded.binary);
                const pcm = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
                this.onLiveInputAudio?.(pcm);
                this.adminHandlers.onLiveInputAudio?.(pcm);
                return;
            }
            this.onPresentationMessage?.(decoded);
        };
        this.socket.onclose = () => {
            this.socket = null;
            this.stopHeartbeat();
            this.updateHealth('offline');
            this.onClose?.();
            if (!this.intentionalClose) {
                this.reconnectTimer = window.setTimeout(() => this.openSocket(), RECONNECT_DELAY_MS);
            }
        };
        this.socket.onerror = () => {
            this.socket?.close();
        };
    }
    handleTextMessage(raw) {
        const parsed = JSON.parse(raw);
        if (parsed.type === HeartbeatMessageTypes.Pong) {
            return;
        }
        switch (parsed.type) {
            case WebSocketSubscribe.EventSnapshot:
                this.adminHandlers.onEventSnapshot?.(parsed.data);
                return;
            case WebSocketSubscribe.EventLiveTelemetry:
                this.adminHandlers.onEventLiveTelemetry?.(parsed.data);
                return;
            case WebSocketSubscribe.EventConnectionAssigned:
                this.adminHandlers.onEventConnectionAssigned?.(parsed.data);
                return;
            default:
                this.onPresentationMessage?.({ metadataJson: raw, binary: new Uint8Array() });
        }
    }
    startHeartbeat() {
        this.stopHeartbeat();
        this.pingTimer = window.setInterval(() => {
            this.sendPing();
        }, PING_INTERVAL_MS);
        this.healthCheckTimer = window.setInterval(() => {
            this.runHealthCheck();
        }, HEALTH_CHECK_INTERVAL_MS);
        this.sendPing();
    }
    stopHeartbeat() {
        if (this.pingTimer !== null) {
            window.clearInterval(this.pingTimer);
            this.pingTimer = null;
        }
        if (this.healthCheckTimer !== null) {
            window.clearInterval(this.healthCheckTimer);
            this.healthCheckTimer = null;
        }
    }
    sendPing() {
        if (this.socket?.readyState !== WebSocket.OPEN) {
            return;
        }
        this.pingSeq += 1;
        this.socket.send(JSON.stringify({
            type: HeartbeatMessageTypes.Ping,
            seq: this.pingSeq,
        }));
    }
    recordServerActivity() {
        this.lastServerActivityMs = Date.now();
        this.updateHealth(this.computeHealth());
    }
    computeHealth() {
        return deriveConnectionHealth({
            socketOpen: this.socket?.readyState === WebSocket.OPEN,
            connectionReady: this.connectionReady,
            lastServerActivityMs: this.lastServerActivityMs,
            firstOpenMs: this.firstOpenMs,
        });
    }
    runHealthCheck() {
        const health = this.computeHealth();
        this.updateHealth(health);
        if (shouldForceReconnect(health) && this.socket?.readyState === WebSocket.OPEN) {
            this.socket.close();
        }
    }
    updateHealth(health) {
        if (health === this.lastHealth) {
            return;
        }
        this.lastHealth = health;
        this.onHealthChange?.(health);
    }
}
/** Default presentation subscribe (server-side defaults when subscribe is omitted). */
export class PresentationWsClient extends EventWsClient {
    constructor(eventId, presenterId) {
        super({ eventId, presenterId });
    }
    connectPresentation(handlers) {
        this.connect({
            onPresentationMessage: handlers.onMessage,
            onOpen: handlers.onOpen,
            onClose: handlers.onClose,
            onHealthChange: handlers.onHealthChange,
        });
    }
    /** @deprecated Use connectPresentation instead. */
    connect(handlers) {
        super.connect({
            onPresentationMessage: handlers.onPresentationMessage,
            admin: handlers.admin,
            onOpen: handlers.onOpen,
            onClose: handlers.onClose,
            onHealthChange: handlers.onHealthChange,
        });
    }
}
